/** 合规实拍图 / 证书 / 代理自动绑定偏好 */

import {
  filterTemplatesByProductCategory,
  pickAutoRuleTemplate,
} from '@/temu-platform/utils/temuTemplateAutoRule';

/** @deprecated 旧类目规则列表；读时用于 soft migrate → enabled */
export const PHOTO_AUTO_BIND_PREF_KEY = 'photoAutoBindRulesByShop';
/** @deprecated 旧类目规则列表；读时用于 soft migrate → enabled */
export const CERT_AUTO_BIND_PREF_KEY = 'certAutoBindRulesByShop';
export const AGENCY_AUTO_BIND_PREF_KEY = 'agencyAutoBindRulesByShop';
export const RRP_AUTO_BIND_PREF_KEY = 'rrpAutoBindRulesByShop';

/** 实拍图自动补传：店铺级开关 */
export const PHOTO_AUTO_BIND_ENABLED_PREF_KEY = 'photoAutoBindEnabledByShop';
/** 证书自动绑定：店铺级开关 */
export const CERT_AUTO_BIND_ENABLED_PREF_KEY = 'certAutoBindEnabledByShop';
/** 自动绑定失败原因：{ [shopId]: { photo?: { [skc]: string }, certificate?: { [skc]: string } } } */
export const COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY = 'complianceAutoBindErrorsByShop';

export function makeComplianceAutoBindRuleId(prefix = 'cab') {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function baseFields(raw, makeId) {
  if (!raw || typeof raw !== 'object') return null;
  const categoryId = String(raw.categoryId ?? '').trim();
  if (!categoryId) return null;
  return {
    id: String(raw.id || makeId()),
    enabled: raw.enabled !== false,
    categoryId,
    categoryPathLabel: String(raw.categoryPathLabel || '').trim(),
    productTypeId: raw.productTypeId != null ? String(raw.productTypeId) : '',
  };
}

export function normalizePhotoAutoBindRule(raw) {
  const base = baseFields(raw, () => makeComplianceAutoBindRuleId('photo'));
  if (!base) return null;
  return {
    ...base,
    templateId: String(raw.templateId ?? '').trim(),
    templateName: String(raw.templateName || '').trim(),
  };
}

export function normalizeRrpAutoBindRule(raw) {
  const base = baseFields(raw, () => makeComplianceAutoBindRuleId('rrp'));
  if (!base) return null;
  return base;
}

export function normalizeCertAutoBindRule(raw) {
  const base = baseFields(raw, () => makeComplianceAutoBindRuleId('cert'));
  if (!base) return null;
  const poolSn = String(raw.poolSn ?? '').trim();
  const certificateTypeId = String(raw.certificateTypeId ?? '').trim();
  if (!poolSn || !certificateTypeId) return null;
  return {
    ...base,
    poolSn,
    poolLabel: String(raw.poolLabel || '').trim(),
    certificateTypeId,
    certificateTypeName: String(raw.certificateTypeName || '').trim(),
  };
}

export function readComplianceAutoBindRules(preferences, prefKey, shopId, normalize) {
  const sid = String(shopId || '').trim();
  if (!sid || !prefKey) return [];
  const root = preferences && typeof preferences === 'object' ? preferences : {};
  const byShop = root[prefKey];
  const list = byShop && typeof byShop === 'object'
    ? (Array.isArray(byShop[sid]) ? byShop[sid] : [])
    : [];
  return list.map(normalize).filter(Boolean);
}

export async function saveComplianceAutoBindRules({
  getPreferences,
  updatePreferences,
  prefKey,
  shopId,
  rules,
  normalize,
}) {
  const sid = String(shopId || '').trim();
  if (!sid) throw new Error('缺少店铺');
  const prefRes = await getPreferences();
  const prefs = prefRes?.preferences || prefRes || {};
  const existingByShop = prefs?.[prefKey] && typeof prefs[prefKey] === 'object'
    ? { ...prefs[prefKey] }
    : {};
  existingByShop[sid] = (Array.isArray(rules) ? rules : []).map(normalize).filter(Boolean);
  await updatePreferences({
    activeShopId: Number(sid) || undefined,
    preferences: { [prefKey]: existingByShop },
  });
  return existingByShop[sid];
}

function shopBoolMap(preferences, prefKey) {
  const root = preferences && typeof preferences === 'object' ? preferences : {};
  const byShop = root[prefKey];
  return byShop && typeof byShop === 'object' ? byShop : {};
}

/**
 * 读店铺自动绑定开关。若新 key 未写过，且旧规则列表存在任意启用规则，则 soft migrate 为 true。
 */
export function readComplianceAutoBindEnabled(preferences, shopId, {
  enabledKey,
  legacyRulesKey,
  normalizeLegacy,
} = {}) {
  const sid = String(shopId || '').trim();
  if (!sid || !enabledKey) return false;
  const byShop = shopBoolMap(preferences, enabledKey);
  if (Object.prototype.hasOwnProperty.call(byShop, sid)) {
    return byShop[sid] === true;
  }
  if (legacyRulesKey && typeof normalizeLegacy === 'function') {
    const legacy = readComplianceAutoBindRules(preferences, legacyRulesKey, sid, normalizeLegacy);
    if (legacy.some((r) => r && r.enabled !== false)) return true;
  }
  return false;
}

export async function saveComplianceAutoBindEnabled({
  getPreferences,
  updatePreferences,
  enabledKey,
  shopId,
  enabled,
}) {
  const sid = String(shopId || '').trim();
  if (!sid || !enabledKey) throw new Error('缺少店铺');
  const prefRes = await getPreferences();
  const prefs = prefRes?.preferences || prefRes || {};
  const existingByShop = shopBoolMap(prefs, enabledKey);
  const next = { ...existingByShop, [sid]: enabled === true };
  await updatePreferences({
    activeShopId: Number(sid) || undefined,
    preferences: { [enabledKey]: next },
  });
  return next[sid];
}

export function readPhotoAutoBindEnabled(preferences, shopId) {
  return readComplianceAutoBindEnabled(preferences, shopId, {
    enabledKey: PHOTO_AUTO_BIND_ENABLED_PREF_KEY,
    legacyRulesKey: PHOTO_AUTO_BIND_PREF_KEY,
    normalizeLegacy: normalizePhotoAutoBindRule,
  });
}

export function readCertAutoBindEnabled(preferences, shopId) {
  return readComplianceAutoBindEnabled(preferences, shopId, {
    enabledKey: CERT_AUTO_BIND_ENABLED_PREF_KEY,
    legacyRulesKey: CERT_AUTO_BIND_PREF_KEY,
    normalizeLegacy: normalizeCertAutoBindRule,
  });
}

/**
 * @param {'photo'|'certificate'} kind
 * @returns {Record<string, string>} skc -> reason
 */
export function readComplianceAutoBindErrors(preferences, shopId, kind = 'photo') {
  const sid = String(shopId || '').trim();
  if (!sid) return {};
  const root = preferences && typeof preferences === 'object' ? preferences : {};
  const byShop = root[COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY];
  const shopBag = byShop && typeof byShop === 'object' ? byShop[sid] : null;
  if (!shopBag || typeof shopBag !== 'object') return {};
  const bucket = shopBag[kind];
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return {};
  const out = {};
  Object.entries(bucket).forEach(([skc, reason]) => {
    const key = String(skc || '').trim();
    const text = String(reason || '').trim();
    if (key && text) out[key] = text;
  });
  return out;
}

/**
 * 合并写入失败原因；pass clearSkcs 在成功后清除。
 * @param {'photo'|'certificate'} kind
 */
export async function saveComplianceAutoBindErrors({
  getPreferences,
  updatePreferences,
  shopId,
  kind = 'photo',
  errorsBySkc = {},
  clearSkcs = [],
}) {
  const sid = String(shopId || '').trim();
  if (!sid) throw new Error('缺少店铺');
  const prefRes = await getPreferences();
  const prefs = prefRes?.preferences || prefRes || {};
  const byShop = prefs?.[COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY]
    && typeof prefs[COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY] === 'object'
    ? { ...prefs[COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY] }
    : {};
  const shopBag = byShop[sid] && typeof byShop[sid] === 'object' ? { ...byShop[sid] } : {};
  const bucket = shopBag[kind] && typeof shopBag[kind] === 'object' && !Array.isArray(shopBag[kind])
    ? { ...shopBag[kind] }
    : {};
  (Array.isArray(clearSkcs) ? clearSkcs : []).forEach((skc) => {
    const key = String(skc || '').trim();
    if (key) delete bucket[key];
  });
  Object.entries(errorsBySkc || {}).forEach(([skc, reason]) => {
    const key = String(skc || '').trim();
    const text = String(reason || '').trim();
    if (!key) return;
    if (text) bucket[key] = text;
    else delete bucket[key];
  });
  shopBag[kind] = bucket;
  byShop[sid] = shopBag;
  await updatePreferences({
    activeShopId: Number(sid) || undefined,
    preferences: { [COMPLIANCE_AUTO_BIND_ERRORS_PREF_KEY]: byShop },
  });
  return bucket;
}

/**
 * 类目匹配：优先精确 categoryId，其次 productTypeId；`*` 为全部类目兜底。
 * 多条命中时：精确类目 > productTypeId > `*`，同级取第一条。
 */
export function pickMatchingAutoBindRule(rules, meta = {}) {
  const list = (Array.isArray(rules) ? rules : []).filter((r) => r && r.enabled !== false);
  if (!list.length) return null;
  const categoryId = String(meta.categoryId || '').trim();
  const productTypeId = String(meta.productTypeId || '').trim();

  const byCat = categoryId
    ? list.filter((r) => String(r.categoryId) === categoryId)
    : [];
  if (byCat.length) return byCat[0];

  const byType = productTypeId
    ? list.filter((r) => String(r.productTypeId || '') === productTypeId && r.categoryId !== '*')
    : [];
  if (byType.length) return byType[0];

  const wildcard = list.filter((r) => r.categoryId === '*' || r.categoryId === 'all');
  return wildcard[0] || null;
}

/**
 * 实拍图库动态匹配：类目范围内开启自动规则的模板，SPU 专属优先。
 */
export function pickPhotoLibraryTemplateForMeta(templates = [], meta = {}) {
  return pickAutoRuleTemplate(filterTemplatesByProductCategory(templates, meta));
}

export function pickMatchingCertAutoBindRule(rules, meta = {}, certificateTypeId = '') {
  const typeId = String(certificateTypeId || '').trim();
  const typed = (Array.isArray(rules) ? rules : []).filter((r) => (
    r && r.enabled !== false && String(r.certificateTypeId || '') === typeId
  ));
  return pickMatchingAutoBindRule(typed, meta);
}

export function normalizeAgencyAutoBindRule(raw) {
  const base = baseFields(raw, () => makeComplianceAutoBindRuleId('agency'));
  if (!base) return null;
  const agencyType = String(raw.agencyType ?? '').trim();
  const agencyId = String(raw.agencyId ?? '').trim();
  if (agencyType === '' || !agencyId) return null;
  return {
    ...base,
    agencyType,
    agencyId,
    agencyName: String(raw.agencyName || '').trim(),
  };
}

/** 代理：先按 agencyType 过滤，再按类目匹配 */
export function pickMatchingAgencyAutoBindRule(rules, meta = {}, agencyType = '') {
  const type = String(agencyType ?? '').trim();
  const typed = (Array.isArray(rules) ? rules : []).filter((r) => (
    r && r.enabled !== false && String(r.agencyType ?? '') === type
  ));
  return pickMatchingAutoBindRule(typed, meta);
}
